/**
 * AgentOsDemoCartRecovery.jsx
 *
 * Public sandbox for the Cart Recovery email generator.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Sparkles, ArrowRight, Loader2, Copy, CheckCircle, Lock, ShoppingCart, Plus, X,
} from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const VOICES = [
  { id: "warm", name: "Vrúcny" }, { id: "formal", name: "Formálny" },
  { id: "friendly", name: "Priateľský" }, { id: "premium", name: "Premium" },
];
const LANGS = [
  { id: "sk", name: "Slovenčina" }, { id: "cs", name: "Čeština" },
  { id: "en", name: "English" }, { id: "de", name: "Deutsch" },
  { id: "pl", name: "Polski" }, { id: "hu", name: "Magyar" },
];

const EXAMPLES = [
  { customer_name: "Karol", cart_items: [{ name: "Bluetooth slúchadlá X3" }, { name: "Nabíjací kábel USB-C" }], cart_value_eur: 89.9, hours_since_abandoned: 3 },
  { customer_name: "Janka", cart_items: [{ name: "Detský kočík AllRoad 3v1" }], cart_value_eur: 459, hours_since_abandoned: 24 },
  { customer_name: "", cart_items: [{ name: "Pánska bunda Stormproof" }, { name: "Zimné rukavice" }, { name: "Čiapka" }], cart_value_eur: 159, hours_since_abandoned: 8 },
];

export default function AgentOsDemoCartRecovery() {
  const [form, setForm] = useState({
    customer_name: "", cart_items: [{ name: "" }],
    cart_value_eur: 0, hours_since_abandoned: 4,
    brand_name: "", brand_voice: "warm", lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });
  const [copied, setCopied] = useState(false);

  const loadExample = (i) => { setForm((f) => ({ ...f, ...EXAMPLES[i] })); setResult(null); };

  const addItem = () => setForm((f) => ({ ...f, cart_items: [...f.cart_items, { name: "" }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, cart_items: f.cart_items.filter((_, idx) => idx !== i) }));
  const setItem = (i, name) => setForm((f) => ({
    ...f, cart_items: f.cart_items.map((it, idx) => idx === i ? { ...it, name } : it),
  }));

  const submit = async (e) => {
    e.preventDefault();
    const filtered = form.cart_items.filter((it) => it.name.trim());
    if (!filtered.length) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/cart-recovery`, {
        ...form, cart_items: filtered,
      });
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const copyAll = async () => {
    if (!result?.output) return;
    const o = result.output;
    const txt = `Subject: ${o.subject}\nPreview: ${o.preview}\n\n${o.body}\n\n[CTA: ${o.cta_label}]`;
    try { await navigator.clipboard.writeText(txt); setCopied(true); } catch {}
    setTimeout(() => setCopied(false), 2000);
  };

  const Left = (
    <form onSubmit={submit} className="space-y-4 p-6 bg-black border border-gray-800">
      <h2 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
        <ShoppingCart className="w-4 h-4" /> Opustený košík
      </h2>

      <div className="flex flex-wrap gap-2">
        <span className="text-xs uppercase tracking-widest text-gray-500 self-center mr-2">Príklad:</span>
        {EXAMPLES.map((ex, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)}
            data-testid={`agent-os-demo-cr-example-${i}`}
            className="px-3 py-1 text-xs bg-[#0a0a0a] border border-gray-800 hover:border-emerald-700/60 text-gray-300 hover:text-emerald-300">
            €{ex.cart_value_eur} · {ex.cart_items.length} pol.
          </button>
        ))}
      </div>

      <input type="text" placeholder="Meno zákazníka (voliteľné)"
        value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
        data-testid="agent-os-demo-cr-customer"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <div>
        <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Položky v košíku</label>
        <div className="space-y-2" data-testid="agent-os-demo-cr-items">
          {form.cart_items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <input type="text" placeholder={`Produkt #${i + 1}`}
                value={it.name} onChange={(e) => setItem(i, e.target.value)}
                data-testid={`agent-os-demo-cr-item-${i}`}
                className="flex-1 px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />
              {form.cart_items.length > 1 && (
                <button type="button" onClick={() => removeItem(i)}
                  data-testid={`agent-os-demo-cr-item-remove-${i}`}
                  className="px-2 border border-gray-800 hover:border-rose-500 text-gray-500 hover:text-rose-300">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={addItem} data-testid="agent-os-demo-cr-add-item"
          className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
          <Plus className="w-3 h-3" /> Pridať položku
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-widest mb-1 block">Hodnota (€)</label>
          <input type="number" min="0" step="0.01" value={form.cart_value_eur}
            onChange={(e) => setForm({ ...form, cart_value_eur: parseFloat(e.target.value) || 0 })}
            data-testid="agent-os-demo-cr-value"
            className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-widest mb-1 block">Hodín od opustenia</label>
          <input type="number" min="0" max="720" value={form.hours_since_abandoned}
            onChange={(e) => setForm({ ...form, hours_since_abandoned: parseInt(e.target.value) || 0 })}
            data-testid="agent-os-demo-cr-hours"
            className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white" />
        </div>
      </div>

      <hr className="border-gray-800" />
      <h3 className="text-sm font-black uppercase tracking-widest text-gray-400">Tvoj brand</h3>
      <input type="text" placeholder="Názov e-shopu"
        value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
        data-testid="agent-os-demo-cr-brand"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <div className="grid grid-cols-2 gap-3">
        <select value={form.brand_voice} onChange={(e) => setForm({ ...form, brand_voice: e.target.value })}
          data-testid="agent-os-demo-cr-voice"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {VOICES.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}
          data-testid="agent-os-demo-cr-lang"
          className="px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {LANGS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      <button type="submit" disabled={status.loading || !form.cart_items.some((it) => it.name.trim())}
        data-testid="agent-os-demo-cr-submit"
        className="w-full px-5 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2">
        {status.loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Generujem…</>
          : <>Vygeneruj recovery email <Sparkles className="w-4 h-4" /></>}
      </button>
    </form>
  );

  const Right = (
    <div className="space-y-4">
      <div className="p-6 bg-gradient-to-br from-emerald-950/30 to-black border-2 border-emerald-700/40 min-h-[300px]">
        <h2 className="text-sm font-black uppercase tracking-widest text-emerald-300 mb-3">AI Recovery Email</h2>

        {!result && !status.loading && !status.error && (
          <div className="text-center py-12 text-gray-500 text-sm" data-testid="agent-os-demo-cr-empty">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-gray-700" /> Vyplň košík a klikni „Vygeneruj"
          </div>
        )}
        {status.loading && (
          <div className="text-center py-12 text-emerald-300 text-sm" data-testid="agent-os-demo-cr-loading">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" /> AI píše email…
          </div>
        )}
        {status.error && (
          <div className="p-4 bg-rose-950/30 border border-rose-700/40 text-rose-200 text-sm" data-testid="agent-os-demo-cr-error">
            <div className="font-bold mb-1">{status.error?.message || "Niečo zlyhalo"}</div>
            {status.error?.cta_url && (
              <a href={status.error.cta_url} className="inline-flex items-center gap-1 mt-2 text-emerald-300 underline text-xs font-bold">
                Registruj sa zadarmo <ArrowRight className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {result?.output && (
          <div data-testid="agent-os-demo-cr-result" className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Subject</div>
              <div className="p-3 bg-black/50 border border-emerald-900/40 text-white font-bold">{result.output.subject}</div>
              <div className="text-[10px] text-gray-500 italic mt-1">Preview: {result.output.preview}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Telo</div>
              <div className="p-3 bg-black/50 border border-emerald-900/40 text-white text-sm whitespace-pre-line leading-relaxed">{result.output.body}</div>
            </div>
            <div className="text-center">
              <span className="inline-block px-5 py-2 bg-emerald-400 text-black font-black text-xs uppercase tracking-widest">{result.output.cta_label}</span>
            </div>
            <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
              <span className="px-2 py-1 bg-gray-900 text-gray-400">{result.latency_ms} ms</span>
              {result.used_fallback && <span className="px-2 py-1 bg-amber-900/30 text-amber-300 font-bold">Fallback</span>}
              <span className="px-2 py-1 bg-gray-900 text-gray-400">Zostáva dnes: {result.remaining_today}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={copyAll} data-testid="agent-os-demo-cr-copy"
                className="flex-1 px-4 py-2 border border-gray-700 hover:border-emerald-400 text-xs font-bold uppercase tracking-widest text-white flex items-center justify-center gap-2">
                {copied ? <><CheckCircle className="w-3 h-3 text-emerald-400" /> Skopírované</> : <><Copy className="w-3 h-3" /> Kopírovať</>}
              </button>
              <a href="/agent-os/onboard" data-testid="agent-os-demo-cr-cta"
                className="flex-1 px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2">
                Aktivovať <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-black border border-gray-800 text-xs text-gray-500 flex gap-3 items-start">
        <Lock className="w-4 h-4 shrink-0 mt-0.5 text-gray-600" />
        <div>Demo má limit 3 generácie / IP / 24h. <a href="/privacy" className="text-emerald-400 underline ml-1">Privacy →</a></div>
      </div>
    </div>
  );

  return (
    <AgentOsDemoShell
      testid="agent-os-demo-cart-recovery-page"
      h1={<>Cart Recovery <span className="text-emerald-400">Agent</span></>}
      lead="Opísaný košík → AI vygeneruje personalizovaný recovery email bez zľavy (chráni maržu). 3 pokusy zadarmo."
      left={Left}
      right={Right}
    />
  );
}
